/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import toast from "react-hot-toast"
import {
  EAdasProgramsLoadingStateTypes, selectAdasProgramsMetaData,
  selectSensingSystemRequirementsDeleteReportDialogOpen,
  selectSensingSystemRequirementsLoadingStateTypeIsLoading,
  selectSensingSystemRequirementsTableRows, selectSensingSystemRequirementsTableSelectedRows,
  selectSensorComponentRequirementsDeleteReportDialogOpen,
  selectSensorComponentRequirementsLoadingStateTypeIsLoading,
  selectSensorComponentRequirementsTableRows, selectSensorComponentRequirementsTableSelectedRows,
  selectWorldModelSolutionRequirementsDeleteReportDialogOpen,
  selectWorldModelSolutionRequirementsLoadingStateTypeIsLoading,
  selectWorldModelSolutionRequirementsTableRows,
  selectWorldModelSolutionRequirementsTableSelectedRows,
  setSensingSystemRequirementsDeleteReportDialogOpen, setSensingSystemRequirementsLoadingState,
  setSensingSystemRequirementsTableSelectedRows,
  setSensorComponentRequirementsDeleteReportDialogOpen, setSensorComponentRequirementsLoadingState,
  setSensorComponentRequirementsTableSelectedRows,
  setWorldModelSolutionRequirementsDeleteReportDialogOpen,
  setWorldModelSolutionRequirementsLoadingState, setWorldModelSolutionRequirementsTableSelectedRows
} from "../../../../../../redux/ducks/AdasPrograms/slice"
import { useAppDispatch, useAppSelector } from "../../../../../../redux/hooks"
import ArgosDialog from "../../../../../components/Dialogs/ArgosDialog"
import { AdasProgramsReportPageIdx, AdasProgramsReportPages } from "../constants"
import { usePostRequest } from "../../../../../../api/helper"
import { CAdasProgramsRequests } from "../../types"
import { useEffect, useMemo, useState } from "react"
import BoxWithCenteredContent from "../../../../../components/Containers/BoxWithCenteredContent"
import ArgosButton, { ArgosLoadingButton } from "../../../../../components/Buttons/ArgosButton"
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox"
import ADAS_PROGRAMS_CONSTANTS from "../../../constants"
import { StringParam, useQueryParam } from "use-query-params"
import { getPageFromHtabIndex } from "../helpers"


export const deleteReportDialogSlice = {
  [AdasProgramsReportPages.SENSING_SYSTEM_REQUIREMENT]: {
    isDialogOpen: selectSensingSystemRequirementsDeleteReportDialogOpen,
    setIsDialogOpen: setSensingSystemRequirementsDeleteReportDialogOpen,
    checkDeleteReportEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.checkDeleteReportEndpoint,
    deleteReportEndpoint: CAdasProgramsRequests.deleteReport.url,
    setLoadingState: setSensingSystemRequirementsLoadingState,
    selectTypeLoadingState: selectSensingSystemRequirementsLoadingStateTypeIsLoading,
    selectRows: selectSensingSystemRequirementsTableRows,
    setSelectedRows: setSensingSystemRequirementsTableSelectedRows,
    selectSelectedRows: selectSensingSystemRequirementsTableSelectedRows,
  },
  [AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT]: {
    isDialogOpen: selectSensorComponentRequirementsDeleteReportDialogOpen,
    setIsDialogOpen: setSensorComponentRequirementsDeleteReportDialogOpen,
    checkDeleteReportEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorComponentRequirements.endpoints.checkDeleteReport,
    deleteReportEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorComponentRequirements.endpoints.deleteReport,
    setLoadingState: setSensorComponentRequirementsLoadingState,
    selectTypeLoadingState: selectSensorComponentRequirementsLoadingStateTypeIsLoading,
    selectRows: selectSensorComponentRequirementsTableRows,
    setSelectedRows: setSensorComponentRequirementsTableSelectedRows,
    selectSelectedRows: selectSensorComponentRequirementsTableSelectedRows,
  },
  [AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT]: {
    isDialogOpen: selectWorldModelSolutionRequirementsDeleteReportDialogOpen,
    setIsDialogOpen: setWorldModelSolutionRequirementsDeleteReportDialogOpen,
    checkDeleteReportEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.worldModelSolutionRequirements.endpoints.checkDeleteReport,
    deleteReportEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.worldModelSolutionRequirements.endpoints.deleteReport,
    setLoadingState: setWorldModelSolutionRequirementsLoadingState,
    selectTypeLoadingState: selectWorldModelSolutionRequirementsLoadingStateTypeIsLoading,
    selectRows: selectWorldModelSolutionRequirementsTableRows,
    setSelectedRows: setWorldModelSolutionRequirementsTableSelectedRows,
    selectSelectedRows: selectWorldModelSolutionRequirementsTableSelectedRows,
  },
}

export const DeleteReportDialog = () => {
  const dispatch = useAppDispatch()

  const htabid = ADAS_PROGRAMS_CONSTANTS.itemDetails.horizontalTabs.pageHtabs.tabIndexUrlKey
  const [htab, setHtab] = useQueryParam(htabid, StringParam)
  const page = useMemo(() => getPageFromHtabIndex(htab as AdasProgramsReportPageIdx), [htab])

  const isOpen = useAppSelector(deleteReportDialogSlice[page].isDialogOpen)
  const onClose = () => {dispatch(deleteReportDialogSlice[page].setIsDialogOpen(false))}

  const adasProgramMetaData = useAppSelector(selectAdasProgramsMetaData)
  const adasProgramId = adasProgramMetaData?.adasProgramId

  const rows = useAppSelector(deleteReportDialogSlice[page].selectRows)
  const selectedRows = useAppSelector(deleteReportDialogSlice[page].selectSelectedRows)
  const [deleteReportDialogData, setDeleteReportDialogData] = useState<DeleteReportDialogData | null>(null)
  const defaultCheckFailedMessage = 'Failed to check if report can be deleted.'

  const isDeletingReport = useAppSelector((state) => deleteReportDialogSlice[page].selectTypeLoadingState(state, EAdasProgramsLoadingStateTypes.DELETE_REPORT))

  type CheckDeleteResponse = {
    shouldDelete: boolean
    message: string
  }

  type DeleteReportDialogData = {
    data: string[]
    message: string
    deleteInvalid?: boolean
  }

  /** Request handles */
  const checkDeleteReportHandle = usePostRequest(deleteReportDialogSlice[page].checkDeleteReportEndpoint)
  const deleteReportHandle = usePostRequest(deleteReportDialogSlice[page].deleteReportEndpoint)

  const checkIfBulkDeleteValid = (reportIds: string[], reportNames: string[]) => {
    dispatch(deleteReportDialogSlice[page].setLoadingState({[EAdasProgramsLoadingStateTypes.DELETE_REPORT]: {isLoading: true}}))
    let _deleteDialogData: DeleteReportDialogData = {
      data: reportNames,
      message: ''
    }
    const body = {adasProgramId, reportIds, reportNames}
    checkDeleteReportHandle(body).then((res: CheckDeleteResponse) => {
      if (res?.shouldDelete) {
        _deleteDialogData.message = res?.message
      } else {
        const errorMsg = res?.message ?? defaultCheckFailedMessage
        _deleteDialogData.deleteInvalid = true
        _deleteDialogData.message = errorMsg
      }
      setDeleteReportDialogData(_deleteDialogData)
    }).catch((err) => {
      console.log(err)
      _deleteDialogData.deleteInvalid = true
      _deleteDialogData.message = defaultCheckFailedMessage
      setDeleteReportDialogData(_deleteDialogData)
    }).finally(() => {
      dispatch(deleteReportDialogSlice[page].setLoadingState({[EAdasProgramsLoadingStateTypes.DELETE_REPORT]: { isLoading: false }}))
    })
  }

  /**
   * @returns A list of report IDs for the selected rows.
   */
  const getSelectedRowReportIds = () => {
    if (selectedRows) {
      return Object.keys(selectedRows)
    }
    return []
  }

  const getSelectedRowReportNames = () => {
    if (selectedRows) {
      const selectedReportIds: string[] = Object.keys(selectedRows)
      const result = rows.filter((row) => selectedReportIds.includes(row.reportId))
      return result.map((row) => row.reportName)
    }
    return []
  }

  /**
   * Deletes the selected reports from the database.
   */
  const networkDeleteReport = () => {
    const ids = getSelectedRowReportIds()
    if (ids) {
      dispatch(setSensingSystemRequirementsLoadingState({[EAdasProgramsLoadingStateTypes.DELETE_REPORT]: {isLoading: true}}))
      deleteReportHandle({
        adasProgramId,
        reportIds: ids,
      }).then(() => {
        setDeleteReportDialogData(null)
        dispatch(deleteReportDialogSlice[page].setSelectedRows({}))
      }).catch((err) => {
        console.log('An error occurred while deleting the report', err)
        toast.error('An error occurred while deleting the report')
      }).finally(() => {
        dispatch(setSensingSystemRequirementsLoadingState({[EAdasProgramsLoadingStateTypes.DELETE_REPORT]: {isLoading: false}}))
        onClose()
      })
    }
  }

  useEffect(() => {
    const reportIds = getSelectedRowReportIds()
    const reportNames = getSelectedRowReportNames()
    checkIfBulkDeleteValid(reportIds, reportNames)
  }, [selectedRows])

  return (
    <ArgosDialog
      width={'auto'}
      height={'auto'}
      open={isOpen}
      onClose={onClose}
      headerTitle="Delete Report"
    >
      <BoxWithCenteredContent horizontal vertical
                              style={{height: '100%', display: 'flex', flexFlow: 'column nowrap'}}>
        {deleteReportDialogData != null && <p style={{width: '300px', textAlign: 'center'}}>
          {deleteReportDialogData?.message}
        </p>}
        <ArgosFlexbox>
          <ArgosButton
            disabled={isDeletingReport}
            onClick={onClose}
          >
            Cancel
          </ArgosButton>
          {!(deleteReportDialogData?.deleteInvalid === true) && <ArgosLoadingButton
            disabled={deleteReportDialogData == null}
            isLoading={isDeletingReport}
            onClick={() => {
              networkDeleteReport()
            }}
          >
            Delete
          </ArgosLoadingButton>}
        </ArgosFlexbox>
      </BoxWithCenteredContent>
    </ArgosDialog>
  )
}
